import React, { useState, useRef, useEffect } from 'react';
import { LayoutElementType, AVAILABLE_ELEMENT_TYPES } from '../../index';
import IconProvider from '@/components/ui/IconProvider';
import { useFloating, autoUpdate, offset, flip, shift, useClick, useDismiss, useRole, FloatingPortal, useInteractions } from '@floating-ui/react';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface AddRowContextMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectWidget: (widgetType: LayoutElementType) => void;
  triggerRef: React.RefObject<HTMLDivElement>;
}

// Widget type descriptions and icons
const widgetInfo: Record<LayoutElementType, { label: string; description: string; icon: string }> = {
  'stats-widget': {
    label: 'Stats Widget',
    description: 'Display key metrics and statistics',
    icon: 'fa-chart-line'
  },
  'row-layout-1': {
    label: 'Row 1 Element',
    description: 'Add a row with 1 element',
    icon: 'fa-table-layout'
  },
  'row-layout-2': {
    label: 'Row 2 Elements',
    description: 'Add a row with 2 elements',
    icon: 'fa-table-layout'
  },
  'row-layout-3': {
    label: 'Row 3 Elements',
    description: 'Add a row with 3 elements',
    icon: 'fa-table-layout'
  },
  'row-layout-4': {
    label: 'Row 4 Elements',
    description: 'Add a row with 4 elements',
    icon: 'fa-table-layout'
  },
  'list-widget': {
    label: 'List Widget',
    description: 'Show a list of items with titles and details',
    icon: 'fa-list'
  },
  'table-widget': {
    label: 'Table Widget',
    description: 'Display data in a structured table format',
    icon: 'fa-table'
  },
  'expandable-table-widget': {
    label: 'Expandable Table Widget',
    description: 'Display data in a table with expandable rows for additional content',
    icon: 'fa-table-cells'
  },
  'title-widget': {
    label: 'Title Widget',
    description: 'Display a title or heading',
    icon: 'fa-heading'
  },
  'text-field-widget': {
    label: 'Text Field Widget',
    description: 'Rich text editor with inline editing, badges, and images',
    icon: 'fa-edit'
  },
  'chart-widget': {
    label: 'Chart Widget',
    description: 'Visualize data with charts and graphs',
    icon: 'fa-chart-bar'
  },
  'form-widget': {
    label: 'Form Widget',
    description: 'Interactive forms for data input',
    icon: 'fa-wpforms'
  },
  'calendar-widget': {
    label: 'Calendar Widget',
    description: 'Display calendar and scheduling information',
    icon: 'fa-calendar'
  },
  'notification-widget': {
    label: 'Notification Widget',
    description: 'Show alerts and notification messages',
    icon: 'fa-bell'
  },
  'progress-widget': {
    label: 'Progress Widget',
    description: 'Display progress bars and completion status',
    icon: 'fa-tasks'
  },
  'media-widget': {
    label: 'Media Widget',
    description: 'Show images, videos, and other media content',
    icon: 'fa-play-circle'
  },

  'grid-widget': {
    label: 'Grid Widget',
    description: 'Display cards in a responsive grid with search and filtering',
    icon: 'fa-th-large',
  },

  'types-widget': {
    label: 'Types Widget',
    description: 'Manage and display game types',
    icon: 'fa-tags',
  },
  'tags-widget': {
    label: 'Tags Widget',
    description: 'Manage and display game tags',
    icon: 'fa-tag',
  },
/*   'document-blueprint-widget': {
    label: 'Document Template Widget',
    description: 'Display a document template',
    icon: 'fa-file-alt',
  }, */
  'tms-table-widget': {
    label: 'TMS Widget',
    description: 'Display localization data in a structured table format',
    icon: 'fa-language'
  },
};

export default function AddRowContextMenu({ 
  isOpen, 
  onClose, 
  onSelectWidget, 
  triggerRef 
}: AddRowContextMenuProps) {
  const { t } = usePlatformTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAll, setShowAll] = useState(false);

  // Floating UI setup
  const { x, y, strategy, refs, context } = useFloating({
    placement: 'bottom-start',
    open: isOpen,
    onOpenChange: (open) => { if (!open) onClose(); },
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  // Sync triggerRef with floating-ui reference
  useEffect(() => {
    if (triggerRef.current) {
      refs.setReference(triggerRef.current);
    }
  }, [triggerRef, refs]);

  // Dismiss on outside click or escape
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'menu' });
  const { getFloatingProps } = useInteractions([click, dismiss, role]);

  // Filter widgets based on search query
  const filteredWidgets = AVAILABLE_ELEMENT_TYPES.filter(widgetType => {
    const info = widgetInfo[widgetType];
    const searchLower = searchQuery.toLowerCase();
    return (
      info.label.toLowerCase().includes(searchLower) ||
      info.description.toLowerCase().includes(searchLower) ||
      widgetType.toLowerCase().includes(searchLower)
    );
  }).filter(widgetType => widgetType in widgetInfo); // Add this filter

  // Show only 5 items initially, or all if showAll is true or search is active
  const displayWidgets = searchQuery || showAll 
    ? filteredWidgets 
    : filteredWidgets.slice(0, 5);

  const hasMoreItems = !searchQuery && !showAll && filteredWidgets.length > 5;

  const handleWidgetSelect = (widgetType: LayoutElementType) => {
    onSelectWidget(widgetType);
    onClose();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setShowAll(false); // Reset show all when searching
  };

  const handleShowAll = () => {
    setShowAll(true);
    setSearchQuery(''); // Clear search when showing all
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        style={{
          position: strategy,
          top: y ?? 0,
          left: x ?? 0,
          zIndex: 1000,
          minWidth: '16rem',
        }}
        className="relative"
        {...getFloatingProps()}
      >
        {/* Black shadow behind the box, offset to bottom and right */}
        <div className="absolute top-1 left-1 w-full h-full bg-black rounded-sm z-0" />
        {/* Main card */}
        <div className="relative w-full bg-white border border-gray-800 rounded-sm z-10 box-border min-w-64">
          <div className="p-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">{t('platform.layout.page-layout.select_widget_type')}</h3>
            <p className="text-xs text-gray-500 mt-1">{t('platform.layout.page-layout.choose_what_type_of_widget_to_add_to_the_new_row')}</p>
            {/* Search Input */}
            <div className="mt-3">
              <div className="relative">
                <IconProvider
                  iconstring="fa-search"
                  size={14}
                  color="#9CA3AF"
                  className="absolute left-3 top-1/2 transform -translate-y-1/2"
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={t('platform.layout.page-layout.search_widgets')}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
          <div className="py-2 max-h-64 overflow-y-auto">
            {displayWidgets.length > 0 ? (
              displayWidgets.map((widgetType) => {
                const info = widgetInfo[widgetType];
                return (
                  <button
                    key={widgetType}
                    onClick={() => handleWidgetSelect(widgetType)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-start space-x-3 group"
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      <IconProvider
                        iconstring={info.icon}
                        size={16}
                        color="#6B7280"
                        className="group-hover:text-blue-600 transition-colors"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                        {info.label}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {info.description}
                      </div>
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No widgets found matching "{searchQuery}"
              </div>
            )}
            {/* Show More Button */}
            {hasMoreItems && (
              <button
                onClick={handleShowAll}
                className="w-full px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition-colors border-t border-gray-100"
              >
                Show all {filteredWidgets.length} widgets
              </button>
            )}
          </div>
          <div className="p-3 border-t border-gray-100">
            <button
              onClick={onClose}
              className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </FloatingPortal>
  );
} 